'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Bars3Icon, XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Accueil', href: '/' },
  { label: 'Parler à Marc', href: '/book-a-session' },
  { label: 'Plans IA', href: '/ai-plans' },
  { label: 'Dictionnaire', href: '/dictionary' },
  { label: 'Grammaire', href: '/grammar-guide' },
];

export default function Header() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  const isCoach =
    user?.email === 'marc@anglaispourvous.fr' ||
    user?.user_metadata?.role === 'admin' ||
    user?.app_metadata?.role === 'admin';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <header
      className={`sticky-header transition-all duration-300 ${
        scrolled
          ? 'bg-[#0F1923]/96 shadow-[0_4px_32px_rgba(0,0,0,0.35)] border-b border-[rgba(201,168,76,0.12)]'
          : 'bg-[#0F1923]/90 border-b border-[rgba(255,255,255,0.06)]'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16 lg:h-[4.5rem]">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <AppLogo size={34} />
            <div className="flex flex-col leading-none">
              <span className="font-extrabold text-base text-white tracking-tight group-hover:text-[#C9A84C] transition-colors duration-200">
                Anglaispourvous
              </span>
              <span className="text-[0.55rem] tracking-[0.18em] uppercase font-semibold" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Immersion · Coaching
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={`nav-${link.href}`}
                href={link.href}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
                  isActive(link.href)
                    ? 'text-[#C9A84C]'
                    : 'text-white/65 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full" style={{ background: '#C9A84C' }} />
                )}
              </Link>
            ))}
            {isCoach && (
              <Link
                href="/coach-dashboard"
                className={`relative ml-2 px-4 py-2 text-sm font-bold transition-all duration-200 rounded-lg flex items-center gap-1.5 border ${
                  isActive('/coach-dashboard')
                    ? 'bg-[#C9A84C] text-[#0F1923] border-[#C9A84C] shadow-[0_0_12px_rgba(201,168,76,0.5)]' :'bg-[rgba(201,168,76,0.15)] text-[#C9A84C] border-[rgba(201,168,76,0.5)] hover:bg-[#C9A84C] hover:text-[#0F1923] hover:border-[#C9A84C] hover:shadow-[0_0_12px_rgba(201,168,76,0.4)]'
                }`}
              >
                <ShieldCheckIcon className="w-4 h-4" />
                Coach
              </Link>
            )}
          </nav>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center gap-2.5">
            <Link
              href="/dashboard"
              className="hidden sm:inline-flex items-center px-4 py-2 btn-primary text-xs rounded-lg tracking-wide"
            >
              Mon Espace
            </Link>
            <Link
              href="/sign-up-login"
              className="hidden sm:inline-flex items-center px-3.5 py-2 text-xs rounded-lg font-semibold transition-colors duration-200"
              style={{ color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.12)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#fff'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.3)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)'; }}
            >
              Connexion
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg transition-colors"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              {mobileOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          mobileOpen ? 'max-h-[30rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-5 pt-2 space-y-1" style={{ background: '#0F1923', borderTop: '1px solid rgba(201,168,76,0.12)' }}>
          {navLinks.map((link) => (
            <Link
              key={`mobile-nav-${link.href}`}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center px-4 py-2.5 rounded-lg text-sm transition-colors ${
                isActive(link.href)
                  ? 'font-semibold' : 'font-medium'
              }`}
              style={{
                color: isActive(link.href) ? '#C9A84C' : 'rgba(255,255,255,0.65)',
                background: isActive(link.href) ? 'rgba(201,168,76,0.08)' : 'transparent',
              }}
            >
              {link.label}
            </Link>
          ))}
          {isCoach && (
            <Link
              href="/coach-dashboard"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 mt-1 border"
              style={{
                color: isActive('/coach-dashboard') ? '#0F1923' : '#C9A84C',
                background: isActive('/coach-dashboard') ? '#C9A84C' : 'rgba(201,168,76,0.15)',
                borderColor: isActive('/coach-dashboard') ? '#C9A84C' : 'rgba(201,168,76,0.5)',
              }}
            >
              <ShieldCheckIcon className="w-5 h-5" />
              🛡 Tableau de bord Coach
            </Link>
          )}
          <Link
            href="/dashboard"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center px-4 py-2.5 btn-primary text-sm rounded-lg mt-3"
          >
            Mon Espace
          </Link>
          <Link
            href="/sign-up-login"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center px-4 py-2.5 text-sm rounded-lg mt-1 font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            Connexion
          </Link>
        </div>
      </div>
    </header>
  );
}